import type { ComponentType, ReactNode, SVGProps } from 'react';
import { Skeleton, Button } from '@heroui/react';
import { cn } from '@/lib/cn';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@/icons';

interface WidgetCardProps {
  title: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** Right-aligned header slot (link, menu, timestamp). */
  action?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  /** Retry handler surfaced in the error state. */
  onRetry?: () => void;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

/**
 * The dashboard's card shell. Owns the three async states every widget shares:
 * a skeleton while loading, a compact retry state on error, and the content
 * otherwise — so each widget component focuses only on its data.
 */
export function WidgetCard({
  title, icon: Icon, action, isLoading, isError, onRetry, className, bodyClassName, children,
}: WidgetCardProps) {
  return (
    <section className={cn('flex flex-col rounded-xl border border-border bg-surface', className)}>
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-fg-subtle" aria-hidden="true" />}
          <h2 className="text-sm font-semibold text-fg">{title}</h2>
        </div>
        {action}
      </header>
      <div className={cn('flex-1 p-4', bodyClassName)}>
        {isLoading ? (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-8 w-2/3 rounded-lg" />
            <Skeleton className="h-4 w-full rounded-lg" />
            <Skeleton className="h-4 w-1/2 rounded-lg" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-warning" />
            <p className="text-sm text-fg-muted">Couldn't load this widget.</p>
            {onRetry && (
              <Button size="sm" variant="flat" startContent={<ArrowPathIcon className="h-4 w-4" />} onPress={onRetry}>
                Retry
              </Button>
            )}
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}
